import React, { useId, useState } from "react";
import {
    Dialog,
    DialogTrigger,
    Popover,
    VisuallyHidden,
} from "react-aria-components";
import { t } from "ttag";

import type { ProductHotspotBlock as Value } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";

import { Themed } from "../../styles/themes";
import { delegateClickToLink } from "../../utils/delegateClickToLink";
import { Clickable } from "../Clickables";
import { FavoriteToggleButton } from "../FavoriteToggleButton";

import styles from "./index.module.scss";

type Product = NonNullable<Value["products"]>[number];

const dotLabel = (product: Product): string =>
    t`View details for ${product.name}`;

const HotspotPopoverContent = ({ product }: { product: Product }) => (
    <Themed
        theme="darkThemeText"
        className={product.url ? styles.cardClickable : styles.card}
        onClick={delegateClickToLink}
    >
        {product.image && (
            <ImageChooserBlock
                value={product.image}
                attrs={{ className: styles.cardPicture }}
                imageAttrs={{ className: styles.cardImage, alt: "" }}
            />
        )}
        <div className={styles.content}>
            <p className={styles.sku}>{product.sku}</p>
            <p className={styles.name}>
                {product.url ? (
                    <Clickable href={product.url}>{product.name}</Clickable>
                ) : (
                    product.name
                )}
            </p>
            {product.msrp && <p className={styles.price}>{product.msrp}</p>}
        </div>
        {product.url && (
            <FavoriteToggleButton
                product={{
                    name: product.name,
                    sku: product.sku,
                    url: product.url,
                }}
                image={product.image}
                className={styles.favoriteButton}
                svgClassName={styles.favoriteSvg}
            />
        )}
    </Themed>
);

export const ProductHotspots = ({ value }: { value: Value }) => {
    const image = value.hotspot_image?.image;
    const hotspots = value.hotspot_image?.hotspots ?? [];
    const products = value.products ?? [];

    const slotId = useId();
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const activeProduct =
        activeIndex !== null ? products[activeIndex] : undefined;

    if (!image || hotspots.length === 0) return null;

    return (
        <div>
            <div className={styles.imageWrapper}>
                <ImageChooserBlock
                    value={image}
                    attrs={{
                        className: styles.picture,
                        style: {
                            "--aspect-ratio": `${image.width} / ${image.height}`,
                        },
                    }}
                    imageAttrs={{ className: styles.image }}
                    sizes="100vw"
                />

                <ul
                    className={styles.desktopOverlay}
                    aria-label={t`Featured products in this image`}
                >
                    {hotspots.map((hotspot, index) => {
                        const product = products[index];
                        if (!product) return null;
                        return (
                            <li
                                key={hotspot.id}
                                className={styles.hotspot}
                                style={{
                                    "left": `${hotspot.x * 100}%`,
                                    "top": `${hotspot.y * 100}%`,
                                    "--pulse-delay": `${index * -0.7}s`,
                                }}
                            >
                                <DialogTrigger>
                                    <Clickable className={styles.dot}>
                                        <VisuallyHidden>
                                            {dotLabel(product)}
                                        </VisuallyHidden>
                                    </Clickable>
                                    <Popover
                                        className={styles.popover}
                                        isNonModal
                                    >
                                        <Dialog>
                                            <HotspotPopoverContent
                                                product={product}
                                            />
                                        </Dialog>
                                    </Popover>
                                </DialogTrigger>
                            </li>
                        );
                    })}
                </ul>

                <ul
                    className={styles.mobileOverlay}
                    aria-label={t`Featured products in this image`}
                >
                    {hotspots.map((hotspot, index) => {
                        const product = products[index];
                        if (!product) return null;
                        const isActive = activeIndex === index;
                        return (
                            <li
                                key={hotspot.id}
                                className={styles.hotspot}
                                style={{
                                    "left": `${hotspot.x * 100}%`,
                                    "top": `${hotspot.y * 100}%`,
                                    "--pulse-delay": `${index * -0.7}s`,
                                }}
                            >
                                <Clickable
                                    className={styles.dot}
                                    aria-controls={slotId}
                                    aria-expanded={isActive}
                                    onPress={() =>
                                        setActiveIndex(isActive ? null : index)
                                    }
                                >
                                    <VisuallyHidden>
                                        {dotLabel(product)}
                                    </VisuallyHidden>
                                </Clickable>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <div id={slotId} className={styles.mobileSlot}>
                {activeProduct && (
                    <HotspotPopoverContent product={activeProduct} />
                )}
            </div>
        </div>
    );
};
